import {isNil} from 'lodash';

import VisitWebsitePopover from '/component/partial/VisitWebsitePopover';
import {openURL} from '/util/linking.util';

/**
 * Generates a callback to be called when info icon is pressed, sends tealium tracking and onInfoIconPress callback
 *
 * @param {object} params - Parameters
 * @param {array} params.actionButtons - A list of buttons to show in the native menu
 * @param {function} params.showAlert - A callback to contact a user
 *
 * @returns {function} - Callback - A callback to contact a client or coach
 */

export const generateOpenSocials = (
  {popoverShow}: any,
  currentScreen: any,
  url: any,
) => {
  return () => {
    const formattedUrl = url.trim().startsWith('http') ? url : `http://${url}`;
    popoverShow(VisitWebsitePopover, {
      currentScreen: currentScreen,
      onSubmit: () => openURL('url', formattedUrl),
    });
  };
};

export function generateShowOptionAlert({actionButtons, showAlert}: any) {
  return ({phoneNumber, accountEmail}: any) =>
    showAlert({
      title: phoneNumber || accountEmail,
      text: '',
      actionButtons: actionButtons,
      opt: {cancelable: true},
    });
}

export const formatPhoneNumber = (
  phoneNumber: any,
  maskPhoneNumber: boolean,
): string => {
  const formattedNumber = changeNum(phoneNumber);
  if (!formattedNumber) {
    return 'N/A';
  }
  return maskPhoneNumber ? doMaskPhoneNumber(formattedNumber) : formattedNumber;
};

/**
 * Format the phone number based on the length of it. Expected length 10 or 11 digits.
 *
 * @param {string} num - Phone number to format
 *
 * @returns {string} - The formatted string that matches either 1-555-555-5555 or 555-555-5555
 */
export const changeNum = (num: any) => {
  const onlyDigitsPhoneNumber = num?.replace(/[^0-9]/g, '');
  if (onlyDigitsPhoneNumber?.length === 10) {
    return onlyDigitsPhoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  } else if (onlyDigitsPhoneNumber?.length === 11) {
    return onlyDigitsPhoneNumber.replace(
      /(\d{1})(\d{3})(\d{3})(\d{4})/,
      '$1-$2-$3-$4',
    );
  }
  return onlyDigitsPhoneNumber;
};

export const doMaskPhoneNumber = (phoneNumber: string): string => {
  let digitCount = 0;

  let masked = phoneNumber.replace(/\d/g, match => {
    digitCount++;
    return digitCount <= phoneNumber.replace(/[^\d]/g, '').length - 2
      ? '*'
      : match;
  });

  return masked;
};

export const formatEmail = (email: string, maskEmail: boolean): string => {
  if (!email) {
    return 'N/A';
  }
  return maskEmail ? doMaskEmail(email) : email;
};

export const doMaskEmail = (email: string): string => {
  // Split the email into local part and domain
  const [localPart, domain] = email.split('@');

  if (!domain) {
    throw new Error('Invalid email');
  }

  // Extract the top-level domain (TLD) and domain name
  const lastDotIndex = domain.lastIndexOf('.');
  const domainName = domain.slice(0, lastDotIndex); // Get the part before the last dot
  const tld = domain.slice(lastDotIndex + 1); // Get the part after the last dot

  if (!localPart) {
    throw new Error('Invalid email');
  }

  // Format the local part: show first 2 characters, rest as *
  const formattedLocal =
    localPart.slice(0, 2) + '*'.repeat(Math.max(0, localPart.length - 2));

  // Format the domain part: show last 2 characters of domain name, rest as *
  const formattedDomainName =
    '*'.repeat(Math.max(0, domainName.length - 2)) + domainName.slice(-2);

  // Return the formatted email
  return `${formattedLocal}@${formattedDomainName}.${tld}`;
};

/**
 * If an item is passed in, a link is opened up
 *
 * @param {object} params - Parameters
 * @param {string} params.item - The link value to open
 * @param {string} params.type - The type of link being opened
 *
 * @returns {function} - Callback - A callback to contact a client or coach
 */
export function onPress({item, type}: any) {
  if (!isNil(item)) {
    openURL(type, item);
  }
}
